<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';

const props = defineProps({
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const SEARCH_DEBOUNCE_MS = 400;

const { t } = useI18n();
const input = ref(props.modelValue);

const emitSearch = useDebounceFn(value => {
  emit('update:modelValue', value.trim());
}, SEARCH_DEBOUNCE_MS);

watch(
  () => props.modelValue,
  value => {
    if (value.trim() !== input.value.trim()) input.value = value;
  }
);

const onInput = event => {
  input.value = event.target.value;
  emitSearch(input.value);
};

const clear = () => {
  input.value = '';
  emit('update:modelValue', '');
};
</script>

<template>
  <div
    class="relative flex h-8 min-w-36 flex-1 items-center rounded-lg bg-n-alpha-2 sm:w-44 sm:flex-none sm:shrink-0 focus-within:outline focus-within:outline-1 focus-within:outline-n-brand"
  >
    <span
      class="i-lucide-search pointer-events-none absolute size-3.5 text-n-slate-10 ltr:left-2.5 rtl:right-2.5"
    />
    <input
      :value="input"
      type="search"
      data-testid="kanban-search-input"
      :placeholder="t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
      :aria-label="t('KANBAN.FILTERS.SEARCH_PLACEHOLDER')"
      class="reset-base !m-0 !h-full !w-full min-w-0 appearance-none rounded-lg !border-0 !bg-transparent !py-0 !shadow-none !ps-8 !pe-7 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
      @input="onInput"
      @keydown.esc="clear"
    />
    <button
      v-if="input"
      type="button"
      data-testid="kanban-search-clear"
      :aria-label="t('KANBAN.FILTERS.SEARCH_CLEAR')"
      class="absolute flex size-5 items-center justify-center rounded text-n-slate-10 hover:text-n-slate-12 ltr:right-1.5 rtl:left-1.5"
      @click="clear"
    >
      <span class="i-lucide-x size-3.5" />
    </button>
  </div>
</template>
